import { Component, createMemo, Show } from 'solid-js'
import { RootModal } from '/web/shared/Modal'
import { characterStore, userStore, i18nStore } from '/web/store'
import CharacterSelectList, { CharacterSelectItem } from '/web/shared/CharacterSelectList'
import { AppSchema } from '/common/types'
import Button from '/web/shared/Button'
import PageHeader from '/web/shared/PageHeader'
import { HeartPlus, X } from 'lucide-solid'
import { isChatPageMemo } from '/web/shared/hooks'

const ImpersonateModal: Component<{ show: boolean; close: () => void }> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const isChat = isChatPageMemo()

  const state = characterStore((s) => ({
    chars: s.characters,
    chatId: s.activeChatId,
    defaultId: s.defaultImpersonateId,
    impersonating: s.impersonating,
  }))
  const user = userStore((s) => ({ user: s.user }))

  const onSelect = (char?: AppSchema.Character) => {
    const match = char ? state.chars.list.find((c) => c._id === char?._id) : undefined

    characterStore.impersonate(match || char)
    props.close()
  }

  const clearImpersonate = () => {
    characterStore.defaultImpersonate('')
  }

  const currentChar = createMemo(() => {
    if (!isChat()) return
    if (!state.impersonating) return
    return { ...state.impersonating, name: `${state.impersonating.name} (${i18n.t.char_currentLabel})` }
  })

  const defaultChar = createMemo(() => {
    if (!state.defaultId) return
    const char = state.chars.map[state.defaultId]
    if (!char) return

    const next = {
      ...char,
      name: `${char.name.trim()} (${i18n.t.char_defaultLabel})`,
      description: i18n.t.char_defaultForNewConvos,
    }
    return next
  })

  const options = createMemo(() => {
    const curr = currentChar()
    const items = state.chars.list
      .filter((ch) => ch.userId === user.user?._id)
      .map((char) => {
        if (curr?._id !== char._id) return char
        return { ...char, name: `${char.name} (${i18n.t.char_currentLabel})` }
      })

    return items
  })

  return (
    <RootModal
      show={props.show}
      close={props.close}
      maxWidth="half"
      fixedHeight
      title={i18n.t.char_impersonate}
    >
      <PageHeader title="" subPage />
      <div class="flex flex-col justify-center gap-2 text-sm">
        <Show
          when={!state.chatId}
          fallback={<p class="font-bold">{i18n.t.char_currentChatPersona}</p>}
        >
          <p class="font-bold">{i18n.t.char_defaultPersona}</p>
        </Show>
        <p>
          {i18n.t.char_impersonateDescription}
        </p>
        <div class="flex w-full justify-center">
          <Button onClick={() => onSelect()}>{i18n.t.char_useMyProfile}</Button>
        </div>

        <Show when={!!defaultChar()}>
          <CharacterSelectItem
            char={defaultChar()!}
            onClick={onSelect}
            adornment={() => (
              <div class="icon-button" onClick={clearImpersonate}>
                <X />
              </div>
            )}
          />
        </Show>
        <CharacterSelectList items={options()} onSelect={onSelect} adornment={DefaultToggle} />
      </div>
    </RootModal>
  )
}

export default ImpersonateModal

const DefaultToggle: Component<{ char: AppSchema.Character }> = (props) => {
  return (
    <div class="icon-button" onClick={() => characterStore.defaultImpersonate(props.char._id)}>
      <HeartPlus />
    </div>
  )
}
